// ---------------------------------------------------------------------------
// The app's own router (R12c)
//
// Replaces @adornis/router. It imports no page and nothing from Adornis, so
// it loads in Node and in the DOM suite (tests/dom/router.dom.ts says what
// it does, case by case). Its state is a @preact/signals-core signal, which
// functional-lit's `stateful` re-renders on (decision c5: the same copy,
// tests/router-wiring.test.ts).
//
// What it fixes, against today's behaviour (R12 plan, section 4):
//   B1  an unknown path on load: one console.warn, replaceState('/'), and the
//       dashboard like any route — in the frame, behind the guard. The state
//       never throws and is never empty.
//   B2  a trailing slash on load is the same route (match.ts), query kept.
//   B3  a click another handler prevented (the skip link) is left alone.
//   B4  back/forward run the leave hooks too (decision c1); a veto puts the
//       address back with pushState.
//   B5  a navigation to the URL already shown does nothing: no entry, no
//       hooks.
//   B7  only a plain left click on a same-origin <a href> to a route, without
//       target or download, is taken; everything else stays the browser's.
//   B13 a leave hook is removed by the function that registered it.
//   B14 params arrive decoded (match.ts).
// And: concurrent navigations share one run of the leave hooks, and only the
// latest commits; a goTo with a missing id warns and stays. The latest also
// wins when it asks for the page shown (a goTo to it, or a Forward back onto
// it while a Back still waits for its hooks), and whatever wins or vetoes,
// the address ends on the page shown.
//
// A popstate or hashchange that leaves the path and the query as they are is
// no navigation (the browser fires popstate for fragments too): no hooks, no
// page change, only the hash in the state.
//
// What a page change does to the title, <html lang>, scroll and focus is
// page-change.ts's; mounting the page again on every change of name or params
// is remount.ts's.
// ---------------------------------------------------------------------------

import { type ReadonlySignal, signal } from '@preact/signals-core';

import { isReservedPath, matchRoute, normalizePath, type Params, pathFor } from './match.js';
import type { RouteTable } from './routes.js';

export interface RouteState<Name extends string = string> {
  name: Name;
  params: Params;
  /** The normalised path. */
  path: string;
  /** The query, with its '?' ('' without one). */
  search: string;
  /** The fragment, with its '#' ('' without one). */
  hash: string;
}

/**
 * How a state came to be: the first one at start(); a goTo (or a taken link click) that pushed or
 * replaced; the browser's back/forward; a change of the fragment only.
 */
export type CommitKind = 'start' | 'push' | 'replace' | 'pop' | 'hash';

export interface Commit<Name extends string = string> {
  state: RouteState<Name>;
  /** The state before; null for the first one. */
  previous: RouteState<Name> | null;
  kind: CommitKind;
  /** True when the address matched nothing and the fallback route was shown instead. */
  fallback: boolean;
}

/** Runs before the view changes; `false` (or a throw) keeps the page. */
export type LeaveHook = () => unknown;

export interface GoToOptions {
  params?: Readonly<Record<string, string | null | undefined>>;
  /** Kept only when given, as before (routing-state-signal.js:96-101). */
  searchParams?: string | URLSearchParams | Readonly<Record<string, string>>;
  hash?: string;
  replaceState?: boolean;
}

export interface Router<Name extends string = string> {
  /** The current route; read it in a render to re-render on navigation. */
  readonly state: ReadonlySignal<RouteState<Name>>;
  /** The path of a route, for an href; '' (and a warning) when a param is missing. */
  href(name: Name, params?: GoToOptions['params']): string;
  /** Reads the address, takes over links and back/forward. Once per page; again after dispose(). */
  start(): void;
  /** Lets go of the window again, and drops the leave hooks. */
  dispose(): void;
  /** Resolves true once the new page is committed; false when nothing changed. */
  goTo(name: Name, options?: GoToOptions): Promise<boolean>;
  /** Registers a hook that runs before every navigation; returns the function that removes it. */
  onBeforeLeave(hook: LeaveHook): () => void;
  /**
   * Runs the leave hooks, sharing a run already in flight; resolves false when one vetoed. For
   * leaving without a route change (R12d's sign-out).
   */
  runLeaveHooks(): Promise<boolean>;
  /** Called after every commit, page change or not. */
  onCommit(listener: (commit: Commit<Name>) => void): () => void;
  /** How many leave hooks are registered now. */
  readonly leaveHookCount: number;
}

export interface RouterOptions<Name extends string> {
  /** The window to read and drive; the global one when left out (read at start()). */
  win?: Window;
  /** The route an unknown path shows; the one at '/' when left out. */
  fallback?: Name;
  warn?: (...args: unknown[]) => void;
}

/** The key of a page: its name and its params. A change of either is a page change (decision c3). */
export function pageKey(state: Pick<RouteState, 'name' | 'params'>): string {
  return `${state.name} ${JSON.stringify(state.params)}`;
}

function searchOf(value: GoToOptions['searchParams']): string {
  if (value === undefined) return '';
  const query = new URLSearchParams(value as ConstructorParameters<typeof URLSearchParams>[0]).toString();
  return query ? `?${query}` : '';
}

const hashOf = (value: string | undefined) => (!value ? '' : value.startsWith('#') ? value : `#${value}`);

export function createRouter<const T extends RouteTable>(
  routes: T,
  options: RouterOptions<Extract<keyof T, string>> = {},
): Router<Extract<keyof T, string>> {
  type Name = Extract<keyof T, string>;
  const warn = options.warn ?? ((...args: unknown[]) => console.warn(...args));
  const fallback = (options.fallback ??
    (Object.keys(routes).find(name => routes[name]?.path === '/') as Name | undefined)) as Name;
  if (!fallback || !routes[fallback]) throw new Error('createRouter: no fallback route (none has the path /)');

  const home: RouteState<Name> = { name: fallback, params: {}, path: '/', search: '', hash: '' };
  const state = signal<RouteState<Name>>(home);
  const hooks = new Set<LeaveHook>();
  const listeners = new Set<(commit: Commit<Name>) => void>();
  let win: Window | undefined;
  let started = false;
  let hookRun: Promise<boolean> | null = null;
  /** Bumped by every navigation that starts; one that finds it moved on was superseded. */
  let ticket = 0;

  const url = (s: Pick<RouteState, 'path' | 'search' | 'hash'>) => s.path + s.search + s.hash;

  function commit(next: RouteState<Name>, kind: CommitKind, fallbackUsed = false) {
    const previous = kind === 'start' ? null : state.peek();
    state.value = next;
    for (const listener of [...listeners]) {
      try {
        listener({ state: next, previous, kind, fallback: fallbackUsed });
      } catch (error) {
        console.error('[router] a commit listener failed', error);
      }
    }
  }

  /** The state for an address, or the fallback (with the address put right) when it names no route. */
  function resolve(path: string, search: string, hash: string): { next: RouteState<Name>; fallback: boolean } {
    const match = matchRoute(path, routes);
    if (match) return { next: { name: match.name as Name, params: match.params, path, search, hash }, fallback: false };
    return { next: home, fallback: true };
  }

  function runLeaveHooks(): Promise<boolean> {
    if (!hookRun) {
      const running = [...hooks].map(hook => Promise.resolve().then(hook));
      hookRun = Promise.allSettled(running)
        .then(results =>
          results.every(r => {
            if (r.status === 'rejected') console.error('[router] a leave hook failed; staying', r.reason);
            return r.status === 'fulfilled' && r.value !== false;
          }),
        )
        .finally(() => {
          hookRun = null;
        });
    }
    return hookRun;
  }

  async function navigate(target: { path: string; search: string; hash: string }, replace: boolean): Promise<boolean> {
    const w = win ?? window;
    const here = () => w.location.pathname + w.location.search + w.location.hash;
    if (url(target) === here()) {
      // The address already says it. When it is the page shown, this request is to stay, and as the
      // latest one it supersedes a navigation still waiting for its hooks. Otherwise a back/forward
      // to it is under way, and it goes on.
      if (here() === url(state.peek())) ticket++;
      return false;
    }
    const mine = ++ticket;
    const ok = await runLeaveHooks();
    if (mine !== ticket) return false;
    if (!ok) {
      // A back/forward this overtook has moved the address already; it is superseded, so put the
      // address back to the page shown here.
      if (here() !== url(state.peek())) w.history.pushState(null, '', url(state.peek()));
      return false;
    }
    const { next, fallback: fell } = resolve(target.path, target.search, target.hash);
    const href = fell ? '/' : url(target);
    if (replace) w.history.replaceState(null, '', href);
    else w.history.pushState(null, '', href);
    if (fell) warn('[router] Unknown route, showing the dashboard:', target.path);
    commit(next, replace ? 'replace' : 'push', fell);
    return true;
  }

  async function onPopState() {
    const w = win;
    if (!w) return;
    const raw = w.location.pathname;
    const path = normalizePath(raw);
    const { search, hash } = w.location;
    const current = state.peek();
    if (path === current.path && search === current.search) {
      // Back on the page shown (a fragment, or Forward after a Back still waiting for its hooks): the
      // latest request is to stay, so a navigation still under way is superseded.
      ticket++;
      if (hash !== current.hash) commit({ ...current, hash }, 'hash');
      return;
    }
    // The browser has already moved; the hooks run before the view does (decision c1).
    const mine = ++ticket;
    const ok = await runLeaveHooks();
    if (mine !== ticket) return;
    if (!ok) {
      w.history.pushState(null, '', url(current));
      return;
    }
    const { next, fallback: fell } = resolve(path, search, hash);
    // `raw` is the address this event moved to; nothing has moved it since, or the ticket would have.
    if (fell) {
      warn('[router] Unknown route, showing the dashboard:', raw);
      w.history.replaceState(null, '', '/');
    } else if (path !== raw) {
      w.history.replaceState(w.history.state, '', url(next));
    }
    commit(next, 'pop', fell);
  }

  function onClick(event: Event) {
    const w = win;
    const click = event as MouseEvent;
    if (!w || typeof click.button !== 'number') return;
    if (click.defaultPrevented || click.button !== 0) return;
    if (click.ctrlKey || click.metaKey || click.shiftKey || click.altKey) return;
    const anchor = click.composedPath().find((node): node is HTMLAnchorElement => (node as Element)?.localName === 'a');
    if (!anchor?.hasAttribute('href') || anchor.hasAttribute('download')) return;
    const target = anchor.getAttribute('target');
    if (target && target !== '_self') return;
    let to: URL;
    try {
      to = new URL(anchor.getAttribute('href') as string, w.location.href);
    } catch {
      return;
    }
    if (to.origin !== w.location.origin || (to.protocol !== 'http:' && to.protocol !== 'https:')) return;
    const path = normalizePath(to.pathname);
    if (isReservedPath(path) || !matchRoute(path, routes)) return;
    const here = normalizePath(w.location.pathname);
    // A fragment on this page stays the browser's: it scrolls, and the hashchange is no navigation.
    if (to.hash && path === here && to.search === w.location.search) return;
    click.preventDefault();
    void navigate({ path, search: to.search, hash: to.hash }, false);
  }

  return {
    state,
    get leaveHookCount() {
      return hooks.size;
    },

    href(name, params) {
      const path = pathFor(name, params, routes);
      if (path === null) {
        warn(`[router] ${name} needs its params; got`, params);
        return '';
      }
      return path;
    },

    start() {
      if (started) return;
      win = options.win ?? window;
      started = true;
      const raw = win.location.pathname;
      const path = normalizePath(raw);
      const { search, hash } = win.location;
      const { next, fallback: fell } = resolve(path, search, hash);
      if (fell) {
        warn('[router] Unknown route, showing the dashboard:', raw);
        win.history.replaceState(null, '', '/');
      } else if (path !== raw) {
        win.history.replaceState(win.history.state, '', url(next));
      }
      win.addEventListener('popstate', onPopState);
      win.addEventListener('hashchange', onPopState);
      win.addEventListener('click', onClick);
      commit(next, 'start', fell);
    },

    dispose() {
      if (!started || !win) return;
      win.removeEventListener('popstate', onPopState);
      win.removeEventListener('hashchange', onPopState);
      win.removeEventListener('click', onClick);
      started = false;
      win = undefined;
      ticket++;
      hooks.clear();
    },

    async goTo(name, opts = {}) {
      const path = pathFor(name, opts.params, routes);
      if (path === null) {
        warn(`[router] goTo(${name}) needs its params; staying on`, state.peek().path, opts.params);
        return false;
      }
      return navigate({ path, search: searchOf(opts.searchParams), hash: hashOf(opts.hash) }, !!opts.replaceState);
    },

    onBeforeLeave(hook) {
      // A wrapper of its own, so the same function registered twice is two hooks, and the returned
      // function removes this registration and no other (B13).
      const registration: LeaveHook = () => hook();
      hooks.add(registration);
      return () => {
        hooks.delete(registration);
      };
    },

    runLeaveHooks,

    onCommit(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
